import { create } from 'zustand'
import { immer } from 'zustand/middleware/immer'
import { z } from 'zod'
import { Chatbot } from 'lib/types/generated-types'
import { PageSection } from 'hooks/useAIChat'
import { Json } from 'lib/types/database'

// Helper functions for localStorage
const saveMessagesToLocalStorage = (
  chatbotId: string,
  messages: ChatbotMessage[]
) => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.setItem(
        `chatbot_messages_${chatbotId}`,
        JSON.stringify(messages)
      )
    } catch (error) {
      console.error('Failed to save messages to localStorage:', error)
    }
  }
}

const loadMessagesFromLocalStorage = (
  chatbotId: string
): ChatbotMessage[] | null => {
  if (typeof window !== 'undefined') {
    try {
      const savedMessages = localStorage.getItem(
        `chatbot_messages_${chatbotId}`
      )
      return savedMessages ? JSON.parse(savedMessages) : null
    } catch (error) {
      console.error('Failed to load messages from localStorage:', error)
      return null
    }
  }
  return null
}

const clearMessagesFromLocalStorage = (chatbotId: string) => {
  if (typeof window !== 'undefined') {
    try {
      localStorage.removeItem(`chatbot_messages_${chatbotId}`)
    } catch (error) {
      console.error('Failed to clear messages from localStorage:', error)
    }
  }
}

export const chatbotFormSchema = z.object({
  name: z.string(),

  submission_ids: z.array(z.string()).nullable(),
  use_all_uploads: z.boolean(),

  icon_filename: z.string().nullable(),
  pretty_icon_filename: z.string().nullable(),

  show_status_message: z.boolean(),
  status_message: z.string().nullable(),
  show_status: z.boolean(),

  theme_color: z.string().nullable(),
  ai_text_color: z.string().nullable(),
  ai_background_color: z.string().nullable(),
  human_text_color: z.string().nullable(),
  human_background_color: z.string().nullable(),

  chatbot_representative_name: z.string().nullable(),
  suggested_messages: z.array(z.string()).nullable(),
  starter_messages: z.array(z.string()).nullable(),
  referral_code: z.string().nullable(),
})

export type ChatbotFormTab = 'Chat' | 'Training' | 'Prompts' | 'Embed'

export type ChatbotMessage = {
  role: 'assistant' | 'user'
  content: string
  sources?: PageSection[]
}

export const initialChatbot: Partial<Chatbot> = {
  name: 'Outcast Chatbot',
  icon_filename: 'outcast-logo.png',
  pretty_icon_filename: 'outcast-logo.png',
  bubble_icon_filename: 'outcast-logo.png',
  pretty_bubble_icon_filename: 'outcast-logo.png',
  theme_color: '#5F8EE4',
  ai_text_color: '#000000',
  ai_background_color: '#EAEEF3',
  human_text_color: '#000000',
  human_background_color: '#FFFFFF',
  chatbot_representative_name: 'Outcast',
  suggested_messages: [
    'Find quotes from my episodes that are inspiring',
    'Suggest a content series based on my existing episodes',
    'Extract the main takeaways from my episode on marketing strategies',
    'How can I repurpose my existing content for my newsletter?',
  ],
  starter_messages: [
    'Hi there! I can help you analyze your videos, generate new content ideas, find quotes, and more. What would you like to work on today?',
  ],
  system_message:
    'You are an AI Assistant designed to help content creators leverage their existing video content. Your primary goal is to assist the creator in analyzing their videos, generating new content ideas, finding relevant quotes or sections, and maximizing the value of their content library. Maintain a professional yet friendly tone, ensuring your responses are actionable and practical. Utilize the information from their video transcripts to provide specific, data-driven insights. Focus on helping them create more engaging content, repurpose existing material, and identify content opportunities. When faced with ambiguous questions, seek to clarify their content goals and provide strategic guidance.',
  user_message: 'Creator Query: "{query}"\n\nVideo Context: "{context}"',
  show_status_message: true,
  status_message: 'Online',
  show_status: true,
  created_at: new Date().toISOString(),
  response_text_color: '#000000',
  use_all_uploads: true,
  submissions: {},
  text_color: '#FFFFFF',
  public: false,
  referral_code: null,
  project_id: 0,
  owner: '',
}

interface ChatbotFormStoreProps {
  chatbot: Partial<Chatbot>
  setField: <K extends keyof Chatbot>(field: K, value: Chatbot[K]) => void
  setChatbot: (chatbot: Chatbot) => void
  currentTab: ChatbotFormTab
  setCurrentTab: (tab: ChatbotFormTab) => void
  isLoading: boolean
  setIsLoading: (loading: boolean) => void
  isVisible: boolean
  setIsVisible: (visible: boolean) => void
  messages: ChatbotMessage[]
  setMessages: (messages: ChatbotMessage[]) => void
  loadMessages: (chatbotId: string) => boolean
  setSuggestedMessages: (messages: string[]) => void
  clearMessages: () => void
  iconPresignedUrl: string | null
  bubbleIconPresignedUrl: string | null
  setIconPresignedUrl: (url: string) => void
  setBubbleIconPresignedUrl: (url: string) => void
  setSubmissions: (submissions: any) => void
  getSubmissions: () => Json | null
  reset: () => void
}

export const useChatbotSettingsStore = create<ChatbotFormStoreProps>()(
  immer((set, get) => ({
    chatbot: initialChatbot,
    iconPresignedUrl: null,
    bubbleIconPresignedUrl: null,
    setIconPresignedUrl: (url: string) => {
      set((state) => {
        state.iconPresignedUrl = url
      })
    },
    setBubbleIconPresignedUrl: (url: string) => {
      set((state) => {
        state.bubbleIconPresignedUrl = url
      })
    },
    getSubmissions: () => get().chatbot?.submissions ?? null,
    setSubmissions: (submissions: any) => {
      set((state) => {
        state.chatbot.submissions = submissions
      })
    },
    currentTab: 'Chat',
    isLoading: false,
    isVisible: false,
    setIsVisible: (visible: boolean) => {
      set((state) => {
        state.isVisible = visible
      })
    },
    setIsLoading: (loading: boolean) => {
      set((state) => {
        state.isLoading = loading
      })
    },
    messages: [],
    setMessages: (messages: ChatbotMessage[]) => {
      set((state) => {
        state.messages = messages
        // Save to localStorage if chatbot ID exists
        if (state.chatbot.id) {
          saveMessagesToLocalStorage(state.chatbot.id.toString(), messages)
        }
      })
    },
    loadMessages: (chatbotId: string): boolean => {
      const savedMessages = loadMessagesFromLocalStorage(chatbotId)
      if (savedMessages && savedMessages.length > 0) {
        set((state) => {
          state.messages = savedMessages
        })
        return true
      }
      return false
    },
    suggestedMessages: [],
    setSuggestedMessages: (messages: string[]) => {
      set((state) => {
        state.chatbot.suggested_messages = messages
      })
    },
    clearMessages: () => {
      set((state) => {
        state.messages =
          state.chatbot.starter_messages?.map((message) => ({
            role: 'assistant',
            content: message,
          })) ?? []

        // Clear from localStorage if chatbot ID exists
        if (state.chatbot.id) {
          clearMessagesFromLocalStorage(state.chatbot.id.toString())
        }
      })
    },
    setCurrentTab: (tab: ChatbotFormTab) => {
      set((state) => {
        state.currentTab = tab
      })
    },
    setField: <K extends keyof Chatbot>(field: K, value: Chatbot[K]) => {
      set((state) => {
        state.chatbot[field] = value
      })
    },
    setChatbot: (chatbot: Chatbot) => {
      set((state) => {
        state.chatbot = chatbot

        // Try to load messages from localStorage when chatbot is set
        if (chatbot.id) {
          const savedMessages = loadMessagesFromLocalStorage(
            chatbot.id.toString()
          )
          if (savedMessages && savedMessages.length > 0) {
            state.messages = savedMessages
          } else if (
            chatbot.starter_messages &&
            chatbot.starter_messages.length > 0
          ) {
            // If no saved messages, initialize with starter messages
            state.messages = chatbot.starter_messages.map((message) => ({
              role: 'assistant',
              content: message,
            }))
          }
        }
      })
    },
    reset: () => {
      set((state) => {
        // Clear localStorage for the current chatbot if it exists
        if (state.chatbot.id) {
          clearMessagesFromLocalStorage(state.chatbot.id.toString())
        }

        state.chatbot = initialChatbot
        state.currentTab = 'Chat'
        state.isLoading = false
        state.isVisible = false
        state.messages = []
      })
    },
  }))
)
